import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, X, FileText, Download, Check, Trash2 } from "lucide-react";

const DEFAULT_FORM = { invoice_number: "", company_name: "VEYRONIX", company_email: "", company_phone: "", company_address: "", company_number: "", client_name: "", client_email: "", client_address: "", items: [{ description: "", quantity: 1, unit_price: 0, amount: 0 }], tax_rate: 20, notes: "", due_date: "" };

export const AdminInvoicesTab = ({ API, token, adminInvoices, fetchAllData }) => {
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ ...DEFAULT_FORM });

  const handleCreateAdminInvoice = async (e) => {
    e.preventDefault();
    try {
      const items = invoiceForm.items.map(item => ({ ...item, amount: item.quantity * item.unit_price }));
      const isEditing = !!editingInvoiceId;
      const url = isEditing ? `${API}/admin/invoices/${editingInvoiceId}` : `${API}/admin/invoices`;
      const method = isEditing ? "PUT" : "POST";
      const response = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include", body: JSON.stringify({ ...invoiceForm, items })
      });
      if (response.ok) {
        toast.success(isEditing ? "Invoice updated!" : "Invoice created!");
        setShowCreateInvoice(false); setEditingInvoiceId(null); setInvoiceForm({ ...DEFAULT_FORM });
        fetchAllData();
      } else { const err = await response.json(); toast.error(err.detail || "Failed to save invoice"); }
    } catch { toast.error("Failed to save invoice"); }
  };

  const handleEditAdminInvoice = (inv) => {
    setEditingInvoiceId(inv.invoice_id);
    setInvoiceForm({
      invoice_number: inv.invoice_number || "", company_name: inv.company_name || "VEYRONIX",
      company_email: inv.company_email || "", company_phone: inv.company_phone || "",
      company_address: inv.company_address || "", company_number: inv.company_number || "",
      client_name: inv.client_name || "", client_email: inv.client_email || "",
      client_address: inv.client_address || "",
      items: inv.items?.length > 0 ? inv.items : [{ description: "", quantity: 1, unit_price: 0, amount: 0 }],
      tax_rate: inv.tax_rate ?? 20, notes: inv.notes || "", due_date: inv.due_date || ""
    });
    setShowCreateInvoice(true);
  };

  const handleDeleteAdminInvoice = async (invoiceId) => {
    if (!confirm("Delete this invoice?")) return;
    const response = await fetch(`${API}/admin/invoices/${invoiceId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    if (response.ok) { toast.success("Invoice deleted"); fetchAllData(); }
  };

  const handleMarkPaid = async (invoiceId) => {
    const response = await fetch(`${API}/admin/invoices/${invoiceId}/mark-paid`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    if (response.ok) { toast.success("Marked as paid"); fetchAllData(); }
  };

  const handleDownloadAdminInvoicePdf = async (invoiceId) => {
    const response = await fetch(`${API}/admin/invoices/${invoiceId}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Invoice_${invoiceId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-slate-500">{adminInvoices.length} invoices</p>
        <Button onClick={() => setShowCreateInvoice(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-admin-invoice-btn">
          <Plus className="h-4 w-4 mr-2" />Create Invoice
        </Button>
      </div>

      {showCreateInvoice && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 space-y-4">
          <h3 className="font-heading font-semibold text-lg">{editingInvoiceId ? "Edit Invoice" : "New Invoice"}</h3>
          <form onSubmit={handleCreateAdminInvoice} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Invoice Number *</Label><Input value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm(p => ({...p, invoice_number: e.target.value}))} placeholder="e.g. INV-001" required data-testid="inv-number" /></div>
              <div><Label>Due Date</Label><Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm(p => ({...p, due_date: e.target.value}))} /></div>
              <div><Label>VAT Rate (%)</Label><Input type="number" value={invoiceForm.tax_rate} onChange={(e) => setInvoiceForm(p => ({...p, tax_rate: parseFloat(e.target.value) || 0}))} /></div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-gray-750 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase">From (Your Company)</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Company Name</Label><Input value={invoiceForm.company_name} onChange={(e) => setInvoiceForm(p => ({...p, company_name: e.target.value}))} placeholder="VEYRONIX" /></div>
                <div><Label className="text-xs">Company Email</Label><Input type="email" value={invoiceForm.company_email} onChange={(e) => setInvoiceForm(p => ({...p, company_email: e.target.value}))} /></div>
                <div><Label className="text-xs">Company Phone</Label><Input value={invoiceForm.company_phone} onChange={(e) => setInvoiceForm(p => ({...p, company_phone: e.target.value}))} /></div>
                <div className="col-span-2"><Label className="text-xs">Company Address</Label><Input value={invoiceForm.company_address} onChange={(e) => setInvoiceForm(p => ({...p, company_address: e.target.value}))} /></div>
                <div><Label className="text-xs">Company Number</Label><Input value={invoiceForm.company_number} onChange={(e) => setInvoiceForm(p => ({...p, company_number: e.target.value}))} /></div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-gray-750 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase">Bill To (Client)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Client Name *</Label><Input value={invoiceForm.client_name} onChange={(e) => setInvoiceForm(p => ({...p, client_name: e.target.value}))} required data-testid="inv-client-name" /></div>
                <div><Label className="text-xs">Client Email</Label><Input type="email" value={invoiceForm.client_email} onChange={(e) => setInvoiceForm(p => ({...p, client_email: e.target.value}))} data-testid="inv-client-email" /></div>
                <div className="col-span-2"><Label className="text-xs">Client Address</Label><Input value={invoiceForm.client_address} onChange={(e) => setInvoiceForm(p => ({...p, client_address: e.target.value}))} /></div>
              </div>
            </div>
            <div>
              <Label>Line Items</Label>
              {invoiceForm.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 mt-2">
                  <Input placeholder="Description" value={item.description} onChange={(e) => { const items = [...invoiceForm.items]; items[idx].description = e.target.value; setInvoiceForm(p => ({...p, items})); }} className="flex-1" />
                  <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => { const items = [...invoiceForm.items]; items[idx].quantity = parseInt(e.target.value) || 0; setInvoiceForm(p => ({...p, items})); }} className="w-20" />
                  <Input type="number" step="0.01" placeholder="Price" value={item.unit_price} onChange={(e) => { const items = [...invoiceForm.items]; items[idx].unit_price = parseFloat(e.target.value) || 0; setInvoiceForm(p => ({...p, items})); }} className="w-28" />
                  <span className="text-sm font-medium w-24 text-right">£{(item.quantity * item.unit_price).toFixed(2)}</span>
                  {invoiceForm.items.length > 1 && (<Button type="button" size="sm" variant="ghost" onClick={() => { const items = invoiceForm.items.filter((_, i) => i !== idx); setInvoiceForm(p => ({...p, items})); }}><X className="h-4 w-4 text-red-500" /></Button>)}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setInvoiceForm(p => ({...p, items: [...p.items, {description: "", quantity: 1, unit_price: 0, amount: 0}]}))}><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
            </div>
            <div><Label>Notes</Label><Input value={invoiceForm.notes} onChange={(e) => setInvoiceForm(p => ({...p, notes: e.target.value}))} placeholder="Payment terms, notes..." /></div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => { setShowCreateInvoice(false); setEditingInvoiceId(null); setInvoiceForm({ ...DEFAULT_FORM }); }}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">{editingInvoiceId ? "Update Invoice" : "Create Invoice"}</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-gray-700">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Invoice #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Client</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
            {adminInvoices.map((inv) => (
              <tr key={inv.invoice_id} className="hover:bg-slate-50 dark:hover:bg-gray-750">
                <td className="px-4 py-3 font-medium text-sm">{inv.invoice_number}</td>
                <td className="px-4 py-3"><div className="text-sm font-medium">{inv.client_name}</div><div className="text-xs text-slate-500">{inv.client_email}</div></td>
                <td className="px-4 py-3 text-sm">{inv.issue_date}</td>
                <td className="px-4 py-3 text-sm font-semibold">£{(inv.total || 0).toFixed(2)}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEditAdminInvoice(inv)} title="Edit" data-testid={`edit-inv-${inv.invoice_id}`}><FileText className="h-4 w-4 text-slate-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDownloadAdminInvoicePdf(inv.invoice_id)} title="Download PDF" data-testid={`download-pdf-${inv.invoice_id}`}><Download className="h-4 w-4 text-indigo-600" /></Button>
                    {inv.status !== 'paid' && (<Button size="sm" variant="ghost" onClick={() => handleMarkPaid(inv.invoice_id)} title="Mark as Paid" data-testid={`mark-paid-${inv.invoice_id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>)}
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteAdminInvoice(inv.invoice_id)} title="Delete" data-testid={`delete-inv-${inv.invoice_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {adminInvoices.length === 0 && (<tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">No invoices yet. Create your first invoice above.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
};
